import React, { useEffect, useState } from 'react';
import { BiHighlight, BiMessageSquareDetail } from 'react-icons/bi';
import { IoTrashOutline } from 'react-icons/io5';
import {
    MdClose,
    MdOpenInFull,
    MdCloseFullscreen,
    MdExitToApp,
    MdKeyboardArrowUp,
    MdKeyboardArrowDown,
    MdKeyboardArrowRight,
    MdKeyboardArrowLeft,
    MdOutlineBookmark,
    MdOutlineBookmarkBorder,
    MdOutlineCalculate,
    MdOutlineKeyboard,
    MdOutlineLocationOn,
    MdOutlineMode,
    MdOutlineTimer,
    MdSuperscript,
    MdWarning,
    MdOutlineTimerOff 
} from 'react-icons/md';
import moment from 'moment';
import Button from 'components/Button2';
import TextArea from 'components/TextArea';
import TextInput from 'components/TextInput';
import AnnotationHelper from 'components/AnnotationHelper';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import usePrompt from 'hooks/usePrompt';
import { useAppSelector } from 'hooks/useStore';
import { MULTIPLE_CHOICES, DIGITAL_TIMER_WARNING_TIME } from 'global/constants';
import { getDeviceOS } from 'global/os';
import Reference from './Reference';
import Shortcuts from './Shortcuts';
import MathOpenEnded from './MathOpenEnded';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";

type DigitalModuleProps = {
    moduleName: string;
    subject?: string;
    timeLeft?: number;
    isUntimed?: boolean;
    prompt?: string;
    questions: Array<{
        question: string;
        id: string;
        choices: Array<{
            id: string;
            choice?: string;
            figure?: string;
            isCorrect?: boolean;
        }>;
        passages: Array<{
            id: string;
            introduction?: string;
            passage: string;
            order?: number;
        }>;
        figures: Array<{
            id: string;
            image: string;
            order?: number;
        }>;
    }>;
    questionIndex: number;
    answers: Array<{
        questionId: string;
        answer?: string;
        marked?: boolean;
        crossedOut?: string[];
        annotations: Array<{
            passageId: string;
            startIndex: number;
            endIndex: number;
            annotation: string;
            html?: string;
            preview?: string;
            color?: string;
            underline?: string;
        }>;
        timeSpent?: number;
    }>;
    markQuestion?: () => void;
    crossOutChoice?: (choiceId: string) => void;
    saveAnnotation?: (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => void;
    deleteAnnotation?: (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => void;
    handleChange: (event: any) => void;
    handleSelect: (event: any) => void;
    handleSaveAndExit: () => void;
    handleDiscardAndExit: () => void;
    handleSubmit: () => void;
    navigateQuestion: (index: number) => void;
};

const COLORS = {
    'Yellow': 'yellow',
    'Pink': 'pink',
    'Blue': 'blue'
};

const UNDERLINE = {
    'Solid': 'solid',
    'Dashed': 'dashed',
    'Dotted': 'dotted'
};

function DigitalSATModule({
    moduleName,
    subject,
    timeLeft,
    isUntimed, 
    prompt,
    questions,
    questionIndex,
    answers,
    markQuestion,
    crossOutChoice,
    saveAnnotation,
    deleteAnnotation,
    handleChange,
    handleSelect,
    handleSaveAndExit,
    handleDiscardAndExit,
    handleSubmit,
    navigateQuestion
}: DigitalModuleProps) {
    const [hideTimer, setHideTimer] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(true);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [crossOut, setCrossOut] = useState(false);
    const [calculator, setCalculator] = useState(null);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [calculatorFull, setCalculatorFull] = useState(false);
    const [referenceOpen, setReferenceOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [exitOpen, setExitOpen] = useState(false);
    const [shortcutKeyPressed, setShortcutKeyPressed] = useState(false);
    const [annotate, setAnnotate] = useState(false);
    const [clickedAnnotation, setClickedAnnotation] = useState(null);
    const [annotation, setAnnotation] = useState(null);
    const [annotationColor, setAnnotationColor] = useState(Object.keys(COLORS)[0]);
    const [annotationUnderline, setAnnotationUnderline] = useState(null);
    const [notesOpen, setNotesOpen] = useState(false);
    const [notes, setNotes] = useState(null);
    const { user } = useAppSelector(state => state.user);
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));

    usePrompt(
        'Your work may be lost. Are you sure you want to exit?',
        timeLeft > 0
    );

    const preventNavigation = (event: BeforeUnloadEvent) => {
        if (timeLeft > 0) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const goToQuestion = (index: number) => {
        navigateQuestion(index);

        let passages = document.getElementById('digitalModule__passages');
        let question = document.getElementById('digitalModule__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;

        setClickedAnnotation(null);
        setAnnotation(null);
    }

    const getMathAnswerPreview = (answer: string) => {
        let answerPreview = '';
        let newAnswer = answer;
        let negative = false;
        if (answer[0] === '-') {
            negative = true;
            newAnswer = answer.slice(1);
        }

        for (let i = 0; i < newAnswer.length; i++) {
            if (i === 0 && newAnswer[i] === '.') {
                answerPreview += '0.';
            }
            else if (newAnswer[i] === '/') {
                answerPreview = `\\Large{\\frac{${answerPreview}}{${newAnswer.slice(i + 1)}}}`;
                break;
            }
            else {
                answerPreview += newAnswer[i];
            }
        }

        return (negative ? '$-' : '$') + answerPreview + '$';
    }

    const handleWindowsShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            code
        } = event;

        if (ctrlKey && altKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (ctrlKey && altKey) {
            switch(code) {
                case 'KeyB':
                    if (questionIndex > 0) {
                        goToQuestion(questionIndex - 1);
                    }
                    break;
                case 'KeyX':
                    if (questionIndex < questions.length) {
                        goToQuestion(questionIndex + 1);
                    }
                    // else {
                    //     handleSubmit();
                    // }
                    break;
                case 'KeyT':
                    setHideTimer(!hideTimer);
                    break;
                case 'KeyV':
                    markQuestion();
                    break;
                // case 'KeyA':
                //     if (subject === 'Reading and Writing') {
                //         if (calculatorOpen) setCalculatorOpen(false);
                //         if (shortcutsOpen) setShortcutsOpen(false);
                //         if (exitOpen) setExitOpen(false);
                //     }
                //     break;
                case 'KeyR':
                    if (subject === 'Math') {
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        if (exitOpen) setExitOpen(false);
                        setReferenceOpen(!referenceOpen);
                    }
                    break;
                case 'KeyC':
                    if (subject === 'Math') {
                        if (referenceOpen) setReferenceOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        if (exitOpen) setExitOpen(false);
                        setCalculatorOpen(!calculatorOpen);
                    }
                    break;
                case 'KeyM':
                    if (questions[questionIndex].choices.length > 0) {
                        setCrossOut(!crossOut);
                    }
                    break;
                case 'Digit1':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[0].id);
                    }
                    break;
                case 'Digit2':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[1].id);
                    }
                    break;
                case 'Digit3':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[2].id);
                    }
                    break;
                case 'Digit4':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[3].id);
                    }
                    break;
                default:
                    return;
            }
            
        }
        else if (ctrlKey && shiftKey) {
            switch(code) {
                case 'Digit1':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[0].id);
                    }
                    break;
                case 'Digit2':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[1].id);
                    }
                    break;
                case 'Digit3':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[2].id);
                    }
                    break;
                case 'Digit4':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[3].id);
                    }
                    break;
                default:
                    return;
            }
        }
        else if (ctrlKey) {
            switch(code) {
                case 'KeyH':
                    event.preventDefault();
                    if (subject === 'Reading and Writing') setAnnotate(!annotate);
                    break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const handleMacShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            metaKey,
            code
        } = event;

        if (metaKey && ctrlKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && ctrlKey) {
            switch(code) {
                case 'KeyB':
                    if (questionIndex > 0) {
                        goToQuestion(questionIndex - 1);
                    }
                    break;
                case 'KeyX':
                    if (questionIndex < questions.length) {
                        goToQuestion(questionIndex + 1);
                    }
                    // else {
                    //     handleSubmit();
                    // }
                    break;
                case 'Digit3':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[0].id);
                    }
                    break;
                case 'Digit4':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[1].id);
                    }
                    break;
                case 'Digit5':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[2].id);
                    }
                    break;
                case 'Digit6':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[3].id);
                    }
                    break;
                case 'Digit7':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[0].id);
                    }
                    break;
                case 'Digit8':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[1].id);
                    }
                    break;
                case 'Digit9':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[2].id);
                    }
                    break;
                case 'Digit0':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[3].id);
                    }
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && altKey) {
            switch(code) {
                case 'KeyG':
                    setHideTimer(!hideTimer);
                    break;
                case 'KeyA':
                    if (subject === 'Reading and Writing') {
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        if (exitOpen) setExitOpen(false);
                    }
                    break;
                case 'KeyH':
                    if (subject === 'Math') {
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        if (exitOpen) setExitOpen(false);
                        setReferenceOpen(!referenceOpen);
                    }
                    break;
                case 'KeyC':
                    if (subject === 'Math') {
                        if (referenceOpen) setReferenceOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        if (exitOpen) setExitOpen(false);
                        setCalculatorOpen(!calculatorOpen);
                    }
                    break;
                case 'KeyM':
                    if (questions[questionIndex].choices.length > 0) {
                        setCrossOut(!crossOut);
                    }
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && shiftKey) {
            switch(code) {
                case 'KeyV':
                    markQuestion();
                    break;
                default:
                    return;
            }
        }
        else if (metaKey) {
            switch(code) {
                case 'KeyH':
                    event.preventDefault();
                    if (subject === 'Reading and Writing') setAnnotate(!annotate);
                    break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
    
        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        };
    
        const dragElement = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }
        };
    
        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalModule__popupHeaderClose') || event.target.closest('.digitalModule__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalModule__popupHeaderClose') || event.target.closest('.digitalModule__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    };

    const handleSelectionChange = (_event: Event) => {
        const selection = window.getSelection();
        const passages = document.getElementsByClassName('digitalModule__passageText');

        if (!selection) {
            Array.from(passages).forEach(passage => {
                passage.classList.remove('digitalModule__passageSelection');
            });

            setAnnotation(null);
        }

        const {
            anchorNode,
            anchorOffset,
            focusNode,
            focusOffset
        } = selection;

        if (Array.from(passages).some(passage => passage.contains(anchorNode)) && (
                anchorNode !== focusNode || anchorOffset !== focusOffset
            )) {
            Array.from(passages).forEach(passage => {
                passage.classList.add('digitalModule__passageSelection');
            });
        }
        else {
            Array.from(passages).forEach(passage => {
                passage.classList.remove('digitalModule__passageSelection');
            });

            const notes = document.getElementById('digitalModule__notes');
            if (notes && (notes.contains(anchorNode) || notes.contains(focusNode))) {
                return;
            }

            setAnnotation(null);
        }
    }

    const handleAnnotationMouseDown = (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => {
        const annotations = document.getElementsByClassName('digitalModule__passageAnnotation');

        for (let i = 0; i < annotations.length; i++) {
            annotations[i].classList.add('digitalModule__passageAnnotation--ignore');
        }

        setClickedAnnotation(annotation);
    }

    const handlePassageMouseUp = () => {
        const annotations = document.getElementsByClassName('digitalModule__passageAnnotation');

        for (let i = 0; i < annotations.length; i++) {
            annotations[i].classList.remove('digitalModule__passageAnnotation--ignore');
        }

        const selection = window.getSelection();
        
        const {
            anchorNode,
            anchorOffset,
            focusNode,
            focusOffset
        } = selection;

        if (
            anchorNode === focusNode &&
            anchorOffset === focusOffset
        ) {
            setAnnotation(clickedAnnotation);
        }

        setClickedAnnotation(null);

        const passages = document.getElementsByClassName('digitalModule__passageText');
        const passage = Array.from(passages).find(passage => passage.contains(anchorNode) && passage.contains(focusNode));

        if (!passage) {
            // setAnnotationAlert('Select some text, then press annotate.');
            return;
        }

        let startOffset = 0;
        let endOffset = 0;

        for (const childNode of Array.from(passage.childNodes)) {
            let childLength = childNode.textContent.length;

            if (childNode === anchorNode || childNode.contains(anchorNode)) {
                startOffset += anchorOffset;
                break;
            }
            else {
                startOffset += childLength;
            }
        }

        for (const childNode of Array.from(passage.childNodes)) {
            let childLength = childNode.textContent.length;

            if (childNode === focusNode || childNode.contains(focusNode)) {
                endOffset += focusOffset;
                break;
            }
            else {
                endOffset += childLength;
            }
        }

        if (startOffset > endOffset) {
            const temp = startOffset;
            startOffset = endOffset;
            endOffset = temp;
        }
        else if (startOffset === endOffset) {
            // setAnnotationAlert('Select some text, then press annotate.');
            return;
        }

        let passageHTML = '';
        let passagePreview = '';
        let lastIndex = 0;

        for (let i = 0; i < passage.childNodes.length; i++) {
            const childNode = passage.childNodes[i];

            if (childNode.hasChildNodes()) {
                const { outerHTML: innerHTML, textContent } = childNode.firstChild.parentElement;
                const indexOffset = innerHTML.indexOf(textContent);

                passageHTML += innerHTML.slice(0, indexOffset);

                if (startOffset >= lastIndex && endOffset <= lastIndex + textContent.length) {
                    passageHTML += textContent.slice(0, startOffset - lastIndex);
                    passageHTML += '<mark>';
                    passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                    passageHTML += '</mark>';
                    passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                    // passagePreview += innerHTML.slice(0, indexOffset);
                    passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                    // passagePreview += innerHTML.slice(indexOffset + textContent.length);
                }
                else if (startOffset >= lastIndex && startOffset < lastIndex + textContent.length) {
                    passageHTML += textContent.slice(0, startOffset - lastIndex);
                    passageHTML += '<mark>';
                    passageHTML += textContent.slice(startOffset - lastIndex, textContent.length);
                    passageHTML += '</mark>';
                    // passagePreview += innerHTML.slice(0, indexOffset);
                    passagePreview += textContent.slice(startOffset - lastIndex, textContent.length);
                    // passagePreview += innerHTML.slice(indexOffset + textContent.length);
                }
                else if (endOffset >= lastIndex && endOffset < lastIndex + textContent.length) {
                    passageHTML += '<mark>';
                    passageHTML += textContent.slice(0, endOffset - lastIndex);
                    passageHTML += '</mark>';
                    passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                    // passagePreview += innerHTML.slice(0, indexOffset);
                    passagePreview += textContent.slice(0, endOffset - lastIndex);
                    // passagePreview += innerHTML.slice(indexOffset + textContent.length);
                }
                else if (startOffset <= lastIndex && endOffset >= lastIndex + textContent.length) {
                    passageHTML += '<mark>';
                    passageHTML += textContent;
                    passageHTML += '</mark>';
                    // passagePreview += innerHTML.slice(0, indexOffset);
                    passagePreview += textContent;
                    // passagePreview += innerHTML.slice(indexOffset + textContent.length);
                }
                else {
                    passageHTML += textContent;
                }

                passageHTML += innerHTML.slice(indexOffset + textContent.length);
                
                lastIndex += textContent.length;
            }
            else {
                const { textContent } = childNode;

                if (startOffset >= lastIndex && endOffset <= lastIndex + textContent.length) {
                    passageHTML += textContent.slice(0, startOffset - lastIndex);
                    passageHTML += '<mark>';
                    passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                    passageHTML += '</mark>';
                    passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                    passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                }
                else if (startOffset >= lastIndex && startOffset < lastIndex + textContent.length) {
                    passageHTML += textContent.slice(0, startOffset - lastIndex);
                    passageHTML += '<mark>';
                    passageHTML += textContent.slice(startOffset - lastIndex, textContent.length);
                    passageHTML += '</mark>';
                    passagePreview += textContent.slice(startOffset - lastIndex, textContent.length);
                }
                else if (endOffset >= lastIndex && endOffset < lastIndex + textContent.length) {
                    passageHTML += '<mark>';
                    passageHTML += textContent.slice(0, endOffset - lastIndex);
                    passageHTML += '</mark>';
                    passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                    passagePreview += textContent.slice(0, endOffset - lastIndex);
                }
                else if (startOffset <= lastIndex && endOffset >= lastIndex + textContent.length) {
                    passageHTML += '<mark>';
                    passageHTML += textContent;
                    passageHTML += '</mark>';
                    passagePreview += textContent;
                }
                else {
                    passageHTML += textContent;
                }

                lastIndex += textContent.length;
            }
        }

        if (annotate) {
            Array.from(passages).forEach(passage => {
                passage.classList.remove('digitalModule__passageSelection');
            });
            setAnnotation({
                passageId: passage.id,
                startIndex: startOffset,
                endIndex: endOffset,
                annotation: null,
                html: passageHTML,
                preview: passagePreview,
                color: annotationColor,
                underline: annotationUnderline
            });
            saveAnnotation({
                passageId: passage.id,
                startIndex: startOffset,
                endIndex: endOffset,
                annotation: null,
                html: passageHTML,
                preview: passagePreview,
                color: annotationColor,
                underline: annotationUnderline
            });
        }
        else {
            setAnnotation({
                passageId: passage.id,
                startIndex: startOffset,
                endIndex: endOffset,
                annotation: null,
                html: passageHTML,
                preview: passagePreview
            });
        }
    }

    const removeNotes = (event, ann) => {
        event.stopPropagation();

        saveAnnotation({
            ...ann,
            annotation: null
        });

        setAnnotation({
            ...ann,
            annotation: null
        });
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            if (subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        const underlines = document.querySelectorAll<HTMLElement>('.katex-html .base .underline');
        for (let i = 0; i < underlines.length; i++) {
            if (underlines[i].querySelector('mark')) {
                continue;
            }
            underlines[i].innerHTML = underlines[i].innerText;
        }

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    useEffect(() => {
        let calculatorEl = document.getElementById('calculator');
        setCalculator(Desmos.GraphingCalculator(calculatorEl, {
            notes: false,
            folders: false,
            images: false,
            links: false
        }));

        window.addEventListener('beforeunload', preventNavigation);
        document.addEventListener('selectionchange', handleSelectionChange);
        document.addEventListener('contextmenu', event => event.preventDefault());
        
        setShortcutKeyPressed(true);

        // dragMove(document.getElementById('calculatorDrag'), document.getElementById('calculatorHeader'));
        // dragMove(document.getElementById('referenceDrag'), document.getElementById('referenceHeader'));
        // dragMove(document.getElementById('shortcutsDrag'), document.getElementById('shortcutsHeader'));
        const calculatorDragEl = document.getElementById('calculatorDrag');
        const calculatorHeaderEl = document.getElementById('calculatorHeader');
        if (calculatorDragEl && calculatorHeaderEl) {
            dragMove(calculatorDragEl, calculatorHeaderEl);
        }
    
        const referenceDragEl = document.getElementById('referenceDrag');
        const referenceHeaderEl = document.getElementById('referenceHeader');
        if (referenceDragEl && referenceHeaderEl) {
            dragMove(referenceDragEl, referenceHeaderEl);
        }
    
        const shortcutsDragEl = document.getElementById('shortcutsDrag');
        const shortcutsHeaderEl = document.getElementById('shortcutsHeader');
        if (shortcutsDragEl && shortcutsHeaderEl) {
            dragMove(shortcutsDragEl, shortcutsHeaderEl);
        }

        return () => {
            window.removeEventListener('beforeunload', preventNavigation);
            document.removeEventListener('selectionchange', handleSelectionChange);
        }
    }, []);

    useEffect(() => {
        if (getDeviceOS() === 'windows') {
            document.addEventListener('keydown', handleWindowsShortcutKeys);
        }
        else if (getDeviceOS() === 'mac_os') {
            document.addEventListener('keydown', handleMacShortcutKeys);
        }

        return () => {
            if (getDeviceOS() === 'windows') {
                document.removeEventListener('keydown', handleWindowsShortcutKeys);
            }
            else if (getDeviceOS() === 'mac_os') {
                document.removeEventListener('keydown', handleMacShortcutKeys);
            }
        }
    }, [shortcutKeyPressed]);

    useEffect(() => {
        if (calculatorOpen) {
            calculator.focusFirstExpression();
        }
    }, [calculatorOpen]);

    useEffect(() => {
        renderKatex();
    }, [subject, questionIndex, answers, directionsOpen]);

    useEffect(() => {
        const selectedAnnotation = document.getElementById('digitalModule__passageAnnotation--selected');

        if (selectedAnnotation) {
            const annotationMarks = selectedAnnotation.getElementsByTagName('mark');
            const passageContainer = document.getElementById('digitalModule__passages');
            if (annotationMarks.length > 0) {
                let annotationMark = annotationMarks[0];
                const annotationWidth = annotationMark.offsetWidth;
                
                let leftOffset = 0;
                let topOffset = -passageContainer.scrollTop;
                while (passageContainer.contains(annotationMark)) {
                    leftOffset += annotationMark.offsetLeft;
                    topOffset += annotationMark.offsetTop;

                    annotationMark = annotationMark.parentElement;
                }

                const annotationHelper = document.getElementById('digitalModule__passageAnnotationHelper');
                if (annotationHelper) {
                    let left = leftOffset - annotationHelper.offsetWidth/2 + annotationWidth/2;
                    let top = topOffset - 55;
                    left = Math.max(Math.min(left, passageContainer.offsetWidth - annotationHelper.offsetWidth), 0);
                    annotationHelper.style.left = left + 'px';
                    annotationHelper.style.top = top + 'px';
                }
            }
        }

        const selectedNotes = document.getElementById('digitalModule__notesCard--active');
    
        if (selectedNotes) {
            selectedNotes.scrollIntoView();
        }
    }, [annotation]);

    useEffect(() => {
        const selectedNotes = document.getElementById('digitalModule__notesCard--active');

        if (selectedNotes) {
            selectedNotes.scrollIntoView();
        }
    }, [notesOpen]);

    return (
        <div className='digitalModule'>
            <div className='digitalModule__header'>
                <div className='digitalModule__titleGroup'>
                    <h1 className='digitalModule__title'>
                        {moduleName}
                    </h1>
                    <button className='digitalModule__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                        {directionsOpen
                            ? <u>Directions</u>
                            : <p>Directions</p>
                        }
                        {directionsOpen
                            ? <MdKeyboardArrowUp className='digitalModule__directionsIcon' />
                            : <MdKeyboardArrowDown className='digitalModule__directionsIcon' />
                        }
                    </button>
                </div>
                <div className='digitalModule__timerGroup'>
                    {isUntimed
                    ? <>
                        <MdOutlineTimerOff className='digitalModule__timer'/>
                        <p className='digitalModule__untimedTimer'>Untimed</p>
                    </>
                    : hideTimer
                        ?   <MdOutlineTimer className={timeLeft <= DIGITAL_TIMER_WARNING_TIME
                                ? 'digitalModule__timer--warning'
                                : 'digitalModule__timer'
                            } />
                        :   <p className={timeLeft <= DIGITAL_TIMER_WARNING_TIME
                                ? 'digitalModule__timer--warning'
                                : 'digitalModule__timer'
                            }>
                                {formatTime(timeLeft)}
                            </p>
                    }
                    {!isUntimed &&
                        <button className='digitalModule__hideTimer' onClick={() => setHideTimer(!hideTimer)}>
                            {hideTimer ? 'Show' : 'Hide'}
                        </button>
                    }
                </div>
                <div className='digitalModule__actionGroup'>
                    {subject === 'Reading and Writing' &&
                        <button 
                            className={annotate
                                ? 'digitalModule__action--active'
                                : 'digitalModule__action'
                            }
                            onClick={() => {
                                if (calculatorOpen) setCalculatorOpen(false);
                                if (shortcutsOpen) setShortcutsOpen(false);
                                if (exitOpen) setExitOpen(false);
                                setAnnotate(!annotate);
                            }}
                        >
                            <div className='digitalModule__actionIconGroup'>
                                <BiHighlight className='digitalModule__actionIcon' />
                                <BiMessageSquareDetail className='digitalModule__actionIcon' />
                            </div>
                            {/* <MdOutlineMode className='digitalModule__actionIcon' /> */}
                            <p className='digitalModule__actionText'>Highlights & Notes</p>
                        </button>
                    }
                    {subject === 'Math' && <>
                        <button
                            className={referenceOpen
                                ? 'digitalModule__action--active'
                                : 'digitalModule__action'
                            }
                            onClick={() => {
                                if (calculatorOpen) setCalculatorOpen(false);
                                if (shortcutsOpen) setShortcutsOpen(false);
                                if (exitOpen) setExitOpen(false);
                                setReferenceOpen(!referenceOpen);
                            }}
                        >
                            <MdSuperscript className='digitalModule__actionIcon' />
                            <p className='digitalModule__actionText'>Reference</p>
                        </button>
                        <button 
                            className={calculatorOpen
                                ? 'digitalModule__action--active'
                                : 'digitalModule__action'
                            }
                            onClick={() => {
                                if (referenceOpen) setReferenceOpen(false);
                                if (shortcutsOpen) setShortcutsOpen(false);
                                if (exitOpen) setExitOpen(false);
                                setCalculatorOpen(!calculatorOpen);
                            }}
                        >
                            <MdOutlineCalculate className='digitalModule__actionIcon' />
                            <p className='digitalModule__actionText'>Calculator</p>
                        </button>
                    </>}
                    <button
                        className={shortcutsOpen
                            ? 'digitalModule__action--active'
                            : 'digitalModule__action'
                        }
                        onClick={() => {
                            if (calculatorOpen) setCalculatorOpen(false);
                            if (referenceOpen) setReferenceOpen(false);
                            if (exitOpen) setExitOpen(false);
                            setShortcutsOpen(!shortcutsOpen);
                        }}
                    >
                        <MdOutlineKeyboard className='digitalModule__actionIcon' />
                        <p className='digitalModule__actionText'>Shortcuts</p>
                    </button>
                    <button className='digitalModule__action' onClick={() => {
                        if (referenceOpen) setReferenceOpen(false);
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setExitOpen(!exitOpen);
                    }}>
                        <MdExitToApp className='digitalModule__actionIcon' />
                        <p className='digitalModule__actionText'>Exit</p>
                    </button>
                </div>
                {directionsOpen &&
                    <div className='digitalModule__directions' ref={directionsRef}>
                        <div className='digitalModule__directionsBubble'>
                            <p className='katex digitalModule__directionsText'>
                                {prompt
                                    ? prompt
                                    : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                }
                            </p>
                            <Button
                                className='digitalModule__directionsClose'
                                onClick={() => setDirectionsOpen(false)}
                                secondary
                            >
                                CLOSE
                            </Button>
                        </div>
                    </div>
                }
                <div id='calculatorDrag' className={calculatorOpen
                    ? (calculatorFull
                        ? 'digitalModule__popup--calculatorFull'
                        : 'digitalModule__popup--calculator'
                      )
                    : 'digitalModule__popup--hidden'
                }>
                    <div id='calculatorHeader' className='digitalModule__popupHeader'>
                        <h3 className='digitalModule__popupHeaderText'>Calculator</h3>
                        {calculatorFull
                            ? <MdCloseFullscreen className='digitalModule__popupHeaderFull' onClick={() => setCalculatorFull(false)} />
                            : <MdOpenInFull className='digitalModule__popupHeaderFull' onClick={() => setCalculatorFull(true)} />
                        }
                        <MdClose className='digitalModule__popupHeaderClose' onClick={() => setCalculatorOpen(false)} />
                    </div>
                    <div
                        id='calculator'
                        style={{ width: calculatorFull ? 800 : 400, height: 600 }}
                    ></div>
                </div>
                <div id='referenceDrag' className={referenceOpen
                    ? 'digitalModule__popup--reference'
                    : 'digitalModule__popup--hidden'
                }>
                    <div id='referenceHeader' className='digitalModule__popupHeader'>
                        <h3 className='digitalModule__popupHeaderText'>Reference</h3>
                        <MdClose className='digitalModule__popupHeaderClose' onClick={() => setReferenceOpen(false)} />
                    </div>
                    <Reference />
                </div>
                <div id='shortcutsDrag' className={shortcutsOpen
                    ? 'digitalModule__popup--shortcuts'
                    : 'digitalModule__popup--hidden'
                }>
                    <div id='shortcutsHeader' className='digitalModule__popupHeader'>
                        <h3 className='digitalModule__popupHeaderText'>
                            Keyboard shortcuts{getDeviceOS() === 'windows'
                                ? ': Windows'
                                : getDeviceOS() === 'mac_os'
                                ? ': MacOS'
                                : ''
                            }
                        </h3>
                        <MdClose className='digitalModule__popupHeaderClose' onClick={() => setShortcutsOpen(false)} />
                    </div>
                    <Shortcuts />
                </div>
                <div className={exitOpen
                    ? 'digitalModule__popup--exit'
                    : 'digitalModule__popup--hidden'
                }>
                    <div className='digitalModule__popupHeader'>
                        <h3 className='digitalModule__popupHeaderText'>
                            <MdWarning className='digitalModule__popupHeaderIcon' />
                            Exiting test
                        </h3>
                        <MdClose className='digitalModule__popupHeaderClose' onClick={() => setExitOpen(false)} />
                    </div>
                    <div className='digitalModule__popupBody'>
                        <p className='digitalModule__popupBodyText'>
                            You are about to exit this test. You can save or discard the progress upon exiting.
                        </p>
                        <div className='digitalModule__popupButtonGroup'>
                            <Button
                                className='digitalModule__popupButton'
                                secondary
                                onClick={handleDiscardAndExit}
                            >
                                DISCARD ANSWERS
                            </Button>
                            <Button
                                className='digitalModule__popupButton'
                                onClick={handleSaveAndExit}    
                            >
                                SAVE AND EXIT
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <div className='digitalModule__dottedLine'></div>
            {questionIndex < questions.length
            ?   <div className='digitalModule__test'>
                    {subject === 'Math' && questions[questionIndex].choices.length > 0
                    ?   <></>
                    :   subject === 'Math'
                    ?   <>
                            <div className='digitalModule__material'>
                                <div className='digitalModule__materialContent'>
                                    <div id='digitalModule__passages' className='digitalModule__passages'>
                                        <MathOpenEnded />
                                    </div>
                                </div>
                            </div>
                            <div className='digitalModule__verticalLine'></div>
                        </>
                    :   <>
                            <div
                                id='digitalModule__material'
                                className='digitalModule__material'
                            >
                                <div className='digitalModule__materialContent'>
                                    <div className='digitalModule__passages' id='digitalModule__passages'>
                                        {questions[questionIndex].figures.map(figure => (
                                            <img
                                                className='digitalModule__figure'
                                                key={figure.id}
                                                src={figure.image}
                                                alt={(questionIndex + 1).toString()}
                                                draggable={false}
                                            />
                                        ))}
                                        {questions[questionIndex].passages.map(({ id, passage }) => (
                                            <div key={id} className={annotate
                                                ? `digitalModule__passage digitalModule__passage--${annotationColor ? COLORS[annotationColor] : 'yellow'}`
                                                : 'digitalModule__passage'}>
                                                <p
                                                    id={id}
                                                    className='katex digitalModule__passageText'
                                                    onMouseUp={handlePassageMouseUp}
                                                    onTouchEnd={handlePassageMouseUp}
                                                >
                                                    {passage}
                                                </p>
                                                {answers[questionIndex].annotations.filter(ann => (
                                                    ann.passageId === id
                                                )).map((ann, a) => (
                                                    <div
                                                        // id={annotation === ann && 'digitalModule__passageAnnotation--selected'}
                                                        // className={`katex ${annotation === ann
                                                        //     ? 'digitalModule__passageAnnotation--selected'
                                                        //     : 'digitalModule__passageAnnotation'
                                                        // }`}
                                                        className={`katex digitalModule__passageAnnotation
                                                            ${ann.color && 'digitalModule__passageAnnotation--' + COLORS[ann.color]}
                                                            ${ann.underline && 'digitalModule__passageAnnotation--' + UNDERLINE[ann.underline]}
                                                        `}
                                                        onMouseDown={() => handleAnnotationMouseDown(ann)}
                                                        onTouchStart={() => handleAnnotationMouseDown(ann)}
                                                        // onMouseOver={(event) => handleAnnotationMouseOver(event, `digitalModule__passageAnnotationBubble${a}`)}
                                                        // onMouseOut={(event) => handleAnnotationMouseOut(event, `digitalModule__passageAnnotationBubble${a}`)}
                                                        dangerouslySetInnerHTML={{ __html: ann.html }}
                                                    ></div>
                                                ))}
                                                {annotation && annotation.passageId === id &&
                                                    <div
                                                        id='digitalModule__passageAnnotation--selected'
                                                        className={`katex digitalModule__passageAnnotation--selected
                                                            ${annotation.color && 'digitalModule__passageAnnotation--' + COLORS[annotation.color]}
                                                            ${annotation.underline && 'digitalModule__passageAnnotation--' + UNDERLINE[annotation.underline]}
                                                        `}
                                                        onMouseDown={() => handleAnnotationMouseDown(annotation)}
                                                        onTouchStart={() => handleAnnotationMouseDown(annotation)}
                                                        dangerouslySetInnerHTML={{ __html: annotation.html }}
                                                    ></div>
                                                }
                                            </div>
                                        ))}
                                        {annotation && <AnnotationHelper
                                            id='digitalModule__passageAnnotationHelper'
                                            annotation={annotation}
                                            annotationColor={annotationColor}
                                            annotationUnderline={annotationUnderline}
                                            notesOpen={notesOpen}
                                            setAnnotationColor={setAnnotationColor}
                                            setAnnotationUnderline={setAnnotationUnderline}
                                            setNotesOpen={setNotesOpen}
                                            setAnnotation={setAnnotation}
                                            saveAnnotation={saveAnnotation}
                                            deleteAnnotation={deleteAnnotation}
                                        />}
                                    </div>
                                </div>
                            </div>
                            {answers[questionIndex].annotations.find(annotation => annotation.annotation !== null)
                                ?   (notesOpen
                                        ?   <div id='digitalModule__notes' className='digitalModule__notesBox'>
                                                <div className='digitalModule__notesOpen' onClick={() => setNotesOpen(false)}>
                                                    <MdKeyboardArrowRight />
                                                </div>
                                                <div className='digitalModule__notes'>
                                                    {answers[questionIndex].annotations.filter(ann => 
                                                        ann.annotation !== null
                                                    ).map(ann => (
                                                        <div id={ann.passageId == annotation?.passageId &&
                                                            ann.startIndex === annotation?.startIndex &&
                                                            ann.endIndex === annotation?.endIndex &&
                                                            'digitalModule__notesCard--active'
                                                        } className={(ann.passageId == annotation?.passageId &&
                                                            ann.startIndex === annotation?.startIndex &&
                                                            ann.endIndex === annotation?.endIndex)
                                                            ? 'digitalModule__notesCard--active'
                                                            : 'digitalModule__notesCard'
                                                        } onClick={() => setAnnotation(ann)}>
                                                            <div className={ann.color
                                                                ? `digitalModule__notesCardHeader--${COLORS[ann.color]}`
                                                                : 'digitalModule__notesCardHeader--yellow'
                                                            }>
                                                                <h6 
                                                                    className={ann.underline
                                                                        ? `digitalModule__notesCardTitle--${UNDERLINE[ann.underline]}`
                                                                        : 'digitalModule__notesCardTitle'
                                                                    }
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: ann.preview
                                                                    }}
                                                                />
                                                                <div className='digitalModule__notesCardDelete' onClick={(event) => removeNotes(event, ann)}>
                                                                    <IoTrashOutline />
                                                                </div>
                                                            </div>
                                                            <textarea
                                                                placeholder='Notes are saved automatically.'
                                                                value={ann.annotation}
                                                                maxLength={1024}
                                                                onChange={(event) => {
                                                                    saveAnnotation({
                                                                        ...ann,
                                                                        annotation: event.target.value
                                                                    });
                                                                    setAnnotation({
                                                                        ...ann,
                                                                        annotation: event.target.value
                                                                    });
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        :   <div className='digitalModule__notesBox--closed'>
                                                <div className='digitalModule__notesOpen' onClick={() => setNotesOpen(true)}>
                                                    <MdKeyboardArrowLeft />
                                                </div>
                                            </div>
                                    )
                                :   <div className='digitalModule__verticalLine'></div>
                            }
                            
                        </>
                    }
                    <div className='digitalModule__question' id='digitalModule__question'>
                        <div className='digitalModule__questionContent'>
                            <div className='digitalModule__questionHeader'>
                                <p className='digitalModule__questionNumber'>
                                    {questionIndex + 1}
                                </p>
                                <button className='digitalModule__questionMarkButton' onClick={markQuestion}>
                                    {answers[questionIndex].marked
                                        ? <MdOutlineBookmark className='digitalModule__questionMarkIcon--marked' />
                                        : <MdOutlineBookmarkBorder className='digitalModule__questionMarkIcon' />
                                    }
                                    Mark for review
                                </button>
                                {questions[questionIndex].choices.length > 0 &&
                                    <button
                                        className={crossOut
                                            ? 'digitalModule__crossOutButton--active'
                                            : 'digitalModule__crossOutButton'
                                        }
                                        onClick={() => setCrossOut(!crossOut)}
                                    >
                                        Abc
                                        <div className='digitalModule__crossOutHelper'>
                                            <div className='digitalModule__crossOutHelperBubble'>
                                                <p>Cross out answer choices you think are wrong.</p>
                                            </div>
                                        </div>
                                    </button>
                                }
                            </div>
                            <div className='digitalModule__questionChoices' id='digitalModule__questionChoices'>
                                {subject === 'Math' && questions[questionIndex].figures.map(figure => (
                                    <img
                                        className='digitalModule__figure'
                                        key={figure.id}
                                        src={figure.image}
                                        alt={(questionIndex + 1).toString()}
                                        draggable={false}
                                    />
                                ))}
                                <p className='digitalModule__questionQuestion'>
                                    <span className='katex'>
                                        {questions[questionIndex].question}
                                    </span>
                                </p>
                                {questions[questionIndex].choices.length
                                ?   <>
                                        {questions[questionIndex].choices.map((choice, i) => (
                                            <div key={choice.id} className='digitalModule__questionChoiceWrapper'>
                                                <button
                                                    className={answers[questionIndex].answer == choice.id
                                                        ? 'digitalModule__questionChoice--selected'
                                                        : crossOut && answers[questionIndex].crossedOut.includes(choice.id)
                                                        ? 'digitalModule__questionChoice--crossedOut'
                                                        : 'digitalModule__questionChoice'
                                                    }
                                                    onClick={() => handleSelect(choice.id)}
                                                >
                                                    <div className='digitalModule__questionChoiceOutline'></div>
                                                    <p className='digitalModule__questionChoiceLetter'>
                                                        {MULTIPLE_CHOICES[i]}
                                                    </p>
                                                    {choice.figure
                                                    ?   <img
                                                            className='digitalModule__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure}
                                                            alt={choice.figure}
                                                            draggable={false}
                                                        />
                                                    :   <p className='digitalModule__questionChoiceText'>
                                                            <span className='katex'>
                                                                {choice.choice}
                                                            </span>
                                                        </p>
                                                    }
                                                </button>
                                                {crossOut &&
                                                    <button
                                                        // className={answers[questionIndex].crossedOut[choice.id]
                                                        className={answers[questionIndex].crossedOut.includes(choice.id)
                                                            ? 'digitalModule__crossOutChoice--selected'
                                                            : 'digitalModule__crossOutChoice'
                                                        }
                                                        onClick={() => crossOutChoice(choice.id)}
                                                    >
                                                        {MULTIPLE_CHOICES[i]}
                                                    </button>
                                                }
                                            </div>
                                        ))}
                                    </>
                                :   <>
                                        <TextInput
                                            className='digitalModule__questionAnswer'
                                            inputClassName='digitalModule__questionAnswerInput'
                                            name={moduleName + questionIndex}
                                            value={answers[questionIndex].answer ? answers[questionIndex].answer : ''}
                                            onChange={event => {
                                                const value = event.target.value
                                                    .replace(/[^\d\.\/-]/g, '')
                                                    .replace(/\.[^\d]/g, '.')
                                                    .replace(/\/[^\d]/g, '/')
                                                    .replace(/^\/$/g, '')
                                                    .replace(/^-[^\d\.]$/g, '-');

                                                if (
                                                    !/[^-]+-/g.test(value) &&
                                                    !/\/(.*\/|.*\.)/g.test(value) &&
                                                    !/\.(.*\/|.*\.)/g.test(value) &&
                                                    value.replace('-', '').length <= 5
                                                ) {
                                                    handleChange(value);
                                                }
                                            }}
                                        />
                                        {answers[questionIndex].answer &&
                                            <div className='digitalModule__questionAnswerPreview'>
                                                <p>Answer Preview:</p> 
                                                <p className={`katex ${['.', '/'].includes(answers[questionIndex].answer.slice(-1))
                                                    ? 'digitalModule__questionAnswerPreviewKatex--wrong'
                                                    : 'digitalModule__questionAnswerPreviewKatex'
                                                }`}>
                                                    {getMathAnswerPreview(answers[questionIndex].answer)}
                                                </p>
                                            </div>
                                        }
                                    </>
                                }
                                <div className='digitalModule__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
            :   <div className='digitalModule__review'>
                    <h2 className='digitalModule__reviewTitle'>
                        Check your work
                    </h2>
                    <p className='digitalModule__reviewText'>
                        On test day, you won’t be able to move on to the next module until time expires. 
                        <br/>
                        For these practice questions, you can click <b>Next</b> when you’re ready to move on.
                    </p>
                    <div className='digitalModule__reviewLegends'>
                        <h3 className='digitalModule__reviewModuleName'>{moduleName} Questions</h3>
                        <div className='digitalModule__reviewLegend'>
                            <div className='digitalModule__reviewLegendUnanswered'></div>
                            <p className='digitalModule__reviewLegendText'>
                                Unanswered
                            </p>
                        </div>
                        <div className='digitalModule__reviewLegend'>
                            <MdOutlineBookmark className='digitalModule__reviewLegendMarked' />
                            <p className='digitalModule__reviewLegendText'>
                                For review
                            </p>
                        </div>
                    </div>
                    <div className='digitalModule__reviewItems'>
                        {questions.map((question, i) => (
                            <button
                                key={question.id}
                                className={answers[i].answer
                                    ? 'digitalModule__reviewItem--answered'
                                    : 'digitalModule__reviewItem'
                                }
                                onClick={() => {
                                    goToQuestion(i);
                                    setNavigatorOpen(false);
                                }}
                            >
                                {questionIndex === i
                                    ? <u className='digitalModule__reviewItemNumber'>{i + 1}</u>
                                    : <p className='digitalModule__reviewItemNumber'>{i + 1}</p>
                                }
                                {questionIndex === i && <MdOutlineLocationOn className='digitalModule__reviewItemCurrent' />}
                                {answers[i].marked && <MdOutlineBookmark className='digitalModule__reviewItemMarked' />}
                            </button>
                        ))}
                    </div>
                </div>
            }
            <div className='digitalModule__dottedLine'></div>
            <div className='digitalModule__footer'>
                <p className='digitalModule__tester'>
                    {user.firstName} {user.lastName}
                </p>
                {questionIndex < questions.length &&
                    <button
                        className='digitalModule__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='digitalModule__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='digitalModule__navigatorToggleIcon' />
                        }
                    </button>
                }
                <div className='digitalModule__buttons'>
                    <Button
                        className='digitalModule__button'
                        secondary
                        disabled={questionIndex === 0}
                        onClick={() => goToQuestion(questionIndex - 1)}
                    >
                        BACK
                    </Button>
                    <Button
                        className='digitalModule__button'
                        onClick={() => questionIndex >= questions.length
                            ? handleSubmit()
                            : goToQuestion(questionIndex + 1)
                        }
                    >
                        {questionIndex >= questions.length ? 'SUBMIT' : 'NEXT'}
                    </Button>
                </div>
                {navigatorOpen &&
                    <div className='digitalModule__navigator' ref={navigatorRef}>
                        <div className='digitalModule__navigatorBubble'>
                            <h2 className='digitalModule__navigatorTitle'>
                                {moduleName} Questions
                            </h2>
                            <div className='digitalModule__navigatorLegends'>
                                <div className='digitalModule__navigatorLegend'>
                                    <MdOutlineLocationOn className='digitalModule__navigatorLegendCurrent' />
                                    <p className='digitalModule__navigatorLegendText'>
                                        Current
                                    </p>
                                </div>
                                <div className='digitalModule__navigatorLegend'>
                                    <div className='digitalModule__navigatorLegendUnanswered'></div>
                                    <p className='digitalModule__navigatorLegendText'>
                                        Unanswered
                                    </p>
                                </div>
                                <div className='digitalModule__navigatorLegend'>
                                    <MdOutlineBookmark className='digitalModule__navigatorLegendMarked' />
                                    <p className='digitalModule__navigatorLegendText'>
                                        For review
                                    </p>
                                </div>
                            </div>
                            <div className='digitalModule__navigatorItems'>
                                {questions.map((question, i) => (
                                    <button
                                        key={`${question.id}nav`}
                                        className={answers[i].answer
                                            ? 'digitalModule__navigatorItem--answered'
                                            : 'digitalModule__navigatorItem'
                                        }
                                        onClick={() => {
                                            goToQuestion(i);
                                            setNavigatorOpen(false);
                                        }}
                                    >
                                        {questionIndex === i
                                            ? <u className='digitalModule__navigatorItemNumber'>{i + 1}</u>
                                            : <p className='digitalModule__navigatorItemNumber'>{i + 1}</p>
                                        }
                                        {questionIndex === i && <MdOutlineLocationOn className='digitalModule__navigatorItemCurrent' />}
                                        {answers[i].marked && <MdOutlineBookmark className='digitalModule__navigatorItemMarked' />}
                                    </button>
                                ))}
                            </div>
                            <Button
                                className='digitalModule__navigatorButton'
                                secondary
                                onClick={() => {
                                    goToQuestion(questions.length);
                                    setNavigatorOpen(false);
                                }}
                            >
                                GO TO REVIEW PAGE
                            </Button>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}

export default DigitalSATModule;